import { CRITICAL_LOG_KEYS } from "@/lib/fhir/reference";
import { QUERY_BEARING_BREADCRUMB_FIELDS } from "./redact-content-token";

/**
 * Default-deny scrub for the Sentry channels that carry free text.
 *
 * Sentry holds no BAA, so no PHI may reach it. The credential scrubs in
 * `redact-content-token.ts` and `redact-secret-urls.ts` target known URL shapes. This module
 * targets the opposite case: a value whose content nobody declared. A string that is not a
 * canonical domain id is replaced by a length token, so the shape survives and the content does
 * not.
 *
 * Three channels need it:
 *
 * - `beforeSendLog`. `lib/logger/index.ts` mirrors every `logger.*` call into `Sentry.logger`
 *   with the whole payload as attributes. No other hook sees that stream.
 * - `beforeBreadcrumb`. The browser `Breadcrumbs` integration records `console`, `dom`, `fetch`,
 *   `history`, and `xhr` events. Their `message`, `data.arguments`, and navigation URLs hold
 *   whatever the page passed.
 * - `beforeSend`, for `event.extra`.
 *
 * The log BODY is not redacted. It is the literal a developer wrote at the call site. Data
 * belongs in the payload, which this module denies by default.
 */

/** Token that replaces a redacted string. Keeps the length for triage. */
function redactedText(value: string): string {
  return `[redacted:len=${value.length}]`;
}

const REDACTED_BREADCRUMB_MESSAGE = "[redacted breadcrumb message]";
const REDACTED_QUERY_VALUE = "[redacted]";
const REDACTED_DEPTH = "[redacted:depth]";

/**
 * Attribute keys whose string value ships verbatim.
 *
 * `CRITICAL_LOG_KEYS` holds the canonical domain ids. Sentry and BetterStack queries filter on
 * them, so a redacted id makes the whole log line useless. `entity` is the logger name that
 * `Logger.mirrorToSentry` adds to every payload.
 */
const ALLOWED_ATTRIBUTE_KEYS: readonly string[] = [...CRITICAL_LOG_KEYS, "entity"];

/**
 * Breadcrumb categories that this app writes itself through `Sentry.addBreadcrumb`.
 *
 * Every other category comes from the SDK integrations, which copy page content into the message,
 * so those are redacted. Add a category here only after you read every call site under it and
 * confirm the message carries no member content. Most write a fixed literal.
 *
 * Listed exactly, never by prefix. A prefix would admit a subcategory nobody has read.
 */
const APP_AUTHORED_BREADCRUMB_CATEGORIES: readonly string[] = [
  "ai-chat",
  "ai-chat-threading",
  "avs",
  "cal.booking",
  "collaboration",
  "labs.order",
  "labs.order.billing",
  "labs.order.partner",
  "scribe-generation",
  "team-management",
  "trends",
  "wearables",
];

/** Breadcrumb `data` fields that hold a URL. */
const URL_BEARING_BREADCRUMB_FIELDS = ["url", "from", "to"];

/**
 * Breadcrumb `data` keys whose string value ships verbatim.
 *
 * Every entry is a key the Sentry SDK itself writes, and every value is structurally bounded: an
 * HTTP verb, a status code, or a fixed literal. Read the integration before you add one.
 *
 * - `method` — `fetch` and `xhr` breadcrumbs, from the browser `Breadcrumbs` integration.
 * - `http.method` — the Node `httpIntegration` outgoing-request breadcrumb, which spells it
 *   differently from the browser one.
 * - `status_code` — the same three breadcrumbs. The SDK writes a number, which passes anyway;
 *   the entry covers an integration that writes the code as a string.
 * - `logger` — the `console` breadcrumb, which writes the literal `"console"`.
 *
 * Nothing the app puts on `data` belongs here. A property named by a feature is free text by
 * construction, and `LabPartnerSelector` proved it: a ZIP is one of the 18 HIPAA identifiers.
 *
 * `level` and `type` are NOT on this list. They are top-level breadcrumb fields, not `data` keys,
 * so this scrub never sees them and they reach Sentry untouched.
 */
const ALLOWED_BREADCRUMB_DATA_KEYS: readonly string[] = [
  "method",
  "http.method",
  "status_code",
  "logger",
];

/** Stops a cyclic or very deep payload from running the hook out of stack. */
const MAX_PAYLOAD_DEPTH = 5;

/** Shape of the Sentry log fields this scrub touches. Structural, to avoid the SDK types. */
interface LogWithAttributes {
  attributes?: Record<string, unknown>;
}

/** Shape of the breadcrumb fields this scrub touches. Structural, to avoid the SDK types. */
interface BreadcrumbWithText {
  category?: string;
  data?: Record<string, unknown>;
  message?: string;
}

/** Shape of the event fields this scrub touches. Structural, to avoid the SDK types. */
interface EventWithExtra {
  extra?: Record<string, unknown>;
}

/**
 * Applies the default-deny rule to one value.
 *
 * Numbers and booleans pass: they carry no free text, and counts, durations, and flags are what
 * makes a log line readable. Strings pass only under an allowlisted key.
 */
function scrubValue({
  key,
  value,
  depth,
}: {
  key?: string;
  value: unknown;
  depth: number;
}): unknown {
  if (value === undefined || typeof value === "function" || typeof value === "symbol") {
    return undefined;
  }

  if (value === null || typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    return key && ALLOWED_ATTRIBUTE_KEYS.includes(key) ? value : redactedText(value);
  }

  if (depth >= MAX_PAYLOAD_DEPTH) {
    return REDACTED_DEPTH;
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => scrubValue({ key, value: item, depth: depth + 1 }))
      .filter((item) => item !== undefined);
  }

  if (typeof value === "object") {
    return scrubRecord({ record: value as Record<string, unknown>, depth: depth + 1 });
  }

  return undefined;
}

/** Scrubs every entry of one object and drops the entries that resolve to `undefined`. */
function scrubRecord({
  record,
  depth,
}: {
  record: Record<string, unknown>;
  depth: number;
}): Record<string, unknown> | undefined {
  const entries = Object.entries(record)
    .map(([key, value]) => [key, scrubValue({ key, value, depth })] as const)
    .filter(([, value]) => value !== undefined);

  if (entries.length === 0) {
    return undefined;
  }

  return Object.fromEntries(entries);
}

/**
 * Scrubs an arbitrary structured payload: log attributes, `event.extra`, console arguments.
 */
export function scrubPhiFromPayload(
  payload?: Record<string, unknown> | null,
): Record<string, unknown> | undefined {
  if (!payload) {
    return undefined;
  }

  return scrubRecord({ record: payload, depth: 0 });
}

/** Replaces the value of every `name=value` pair, keeping the names. */
function redactParameterValues(query: string): string {
  return query
    .split("&")
    .map((parameter) => {
      const separatorIndex = parameter.indexOf("=");
      if (separatorIndex === -1) return parameter;
      return `${parameter.slice(0, separatorIndex)}=${REDACTED_QUERY_VALUE}`;
    })
    .join("&");
}

/**
 * Replaces every query parameter value and the fragment of a URL.
 *
 * Parameter names stay, because they say what the request was doing. The values do not:
 * `/welcome?code=…` carries an invite secret and `?composerText=…` carries clinical text.
 * Works on a relative path too, which is the shape a `navigation` breadcrumb reports.
 *
 * Text before a `?` is a path, so this returns a bare query string unchanged. Use
 * `redactQueryStringValues` for a value that is a query string on its own.
 */
export function redactUrlQueryValues(value: string): string {
  const fragmentIndex = value.indexOf("#");
  const hasFragment = fragmentIndex !== -1 && fragmentIndex < value.length - 1;
  const withoutFragment = fragmentIndex === -1 ? value : value.slice(0, fragmentIndex);
  const fragment = hasFragment ? `#${REDACTED_QUERY_VALUE}` : "";

  const queryIndex = withoutFragment.indexOf("?");
  if (queryIndex === -1) {
    return `${withoutFragment}${fragment}`;
  }

  const path = withoutFragment.slice(0, queryIndex);
  const query = redactParameterValues(withoutFragment.slice(queryIndex + 1));

  return `${path}?${query}${fragment}`;
}

/**
 * Replaces every value of a BARE query string: `code=abc&x=1` -> `code=[redacted]&x=[redacted]`.
 *
 * `http.query` and `query_string` hold the query with no path in front of it. A leading `?` is
 * kept when the integration that wrote the field included one.
 */
export function redactQueryStringValues(value: string): string {
  const hasLeadingMark = value.startsWith("?");
  const query = hasLeadingMark ? value.slice(1) : value;

  return `${hasLeadingMark ? "?" : ""}${redactParameterValues(query)}`;
}

/**
 * The `beforeSendLog` hook.
 *
 * This is the only hook on the structured log stream. `beforeSend` sees error events,
 * `beforeSendTransaction` sees transactions, and `beforeBreadcrumb` sees breadcrumbs. None of
 * them sees a `Sentry.logger` call.
 */
export function scrubPhiFromLog<T extends LogWithAttributes>(log: T): T {
  if (!log.attributes) {
    return log;
  }

  return { ...log, attributes: scrubPhiFromPayload(log.attributes) };
}

/**
 * The `beforeBreadcrumb` hook. Compose it after the credential scrubs.
 *
 * `console.info("Intake Data", data)` lands in `data.arguments`, and a `navigation` breadcrumb
 * puts the whole URL on `data.from` and `data.to` — a field neither credential scrub reads.
 *
 * The SDK also copies the query off the URL into `http.query` and `query_string`. A scrub that
 * reads the URL alone therefore leaves `?code=…` and `?composerText=…` on the sibling field, on
 * a breadcrumb whose `data.url` looks clean.
 *
 * The rest of `data` default-denies. An app-authored breadcrumb types its properties as
 * `Record<string, unknown>`, so a call site can put anything there and the SDK ships it verbatim.
 * `ALLOWED_BREADCRUMB_DATA_KEYS` names the few structural keys that survive by value.
 */
export function scrubPhiFromBreadcrumb<T extends BreadcrumbWithText>(breadcrumb: T): T {
  const isAppAuthored =
    typeof breadcrumb.category === "string" &&
    APP_AUTHORED_BREADCRUMB_CATEGORIES.includes(breadcrumb.category);

  const message =
    breadcrumb.message && !isAppAuthored ? REDACTED_BREADCRUMB_MESSAGE : breadcrumb.message;

  if (!breadcrumb.data) {
    return { ...breadcrumb, message };
  }

  const data: Record<string, unknown> = {};

  // The URL and query fields run FIRST, and they keep the parameter names. A blanket token here
  // would cost the one part of the breadcrumb that says what the request was doing:
  // `url: /welcome?code=[redacted]` triages, `url: [redacted:len=88]` does not.
  for (const field of URL_BEARING_BREADCRUMB_FIELDS) {
    const value = breadcrumb.data[field];
    if (typeof value === "string") data[field] = redactUrlQueryValues(value);
  }

  for (const field of QUERY_BEARING_BREADCRUMB_FIELDS) {
    const value = breadcrumb.data[field];
    if (typeof value === "string") data[field] = redactQueryStringValues(value);
  }

  // Everything the loops above did not claim default-denies, including a URL or query field that
  // arrived as a record or a tuple array rather than a string.
  for (const [key, value] of Object.entries(breadcrumb.data)) {
    if (key in data) continue;

    const isStructural = typeof value === "string" && ALLOWED_BREADCRUMB_DATA_KEYS.includes(key);
    const scrubbed = isStructural ? value : scrubValue({ key, value, depth: 0 });
    if (scrubbed !== undefined) data[key] = scrubbed;
  }

  return { ...breadcrumb, data, message };
}

/**
 * The `beforeSend` hook. Compose it after the credential scrubs.
 *
 * Only `extra` is scrubbed. The exception values stay readable, because Sentry groups issues on
 * them, and `contexts.trace` holds the trace ids that tie an event to its transaction.
 */
export function scrubPhiFromEvent<T extends EventWithExtra>(event: T): T {
  if (!event.extra) {
    return event;
  }

  return { ...event, extra: scrubPhiFromPayload(event.extra) };
}
